import { useState } from "react";
import type { FormEvent } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import { EXPORT_FORMAT_DETAILS } from "@/modules/export";
import type { ExportFormat } from "@/modules/export";
import styles from "./export-table-dialog.module.scss";

export const EXPORT_TABLE_TITLE = "Exportar Tabela";
export const EXPORT_TABLE_SECTION_TITLE = "Selecione o formato de saída";

const FORM_ID = "export-table-form";

interface ExportTableDialogProps {
  /** Nome da listagem exportada, usado nos rótulos assistivos do modal. */
  listName: string;
  isExporting: boolean;
  /** Recebe o formato escolhido; cabe ao chamador fechar o modal. */
  onExport: (format: ExportFormat) => void;
  onCancel: () => void;
}

export function ExportTableDialog({
  listName,
  isExporting,
  onExport,
  onCancel,
}: ExportTableDialogProps) {
  const [format, setFormat] = useState<ExportFormat | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!format) return;

    onExport(format);
  }

  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        disabled={isExporting}
        onClick={onCancel}
        data-testid="export-table-cancel"
      />
      <Button
        type="submit"
        form={FORM_ID}
        className={styles.exportButton}
        icon="pi pi-download"
        label="Exportar"
        // AC1: sem formato escolhido não há o que exportar.
        disabled={!format || isExporting}
        loading={isExporting}
        data-testid="export-table-submit"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      contentClassName={styles.content}
      header={EXPORT_TABLE_TITLE}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      dismissableMask={!isExporting}
      closable={!isExporting}
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      breakpoints={{ "48rem": "95vw" }}
      data-testid="export-table-dialog"
    >
      <form id={FORM_ID} className={styles.form} onSubmit={handleSubmit}>
        <h3 className={styles.sectionTitle} id="export-table-formats-title">
          {EXPORT_TABLE_SECTION_TITLE}
        </h3>

        <div
          className={styles.formats}
          role="radiogroup"
          aria-labelledby="export-table-formats-title"
        >
          {EXPORT_FORMAT_DETAILS.map((option) => (
            <label
              key={option.value}
              className={`${styles.format} ${format === option.value ? styles.formatSelected : ""}`}
              htmlFor={`export-format-${option.value}`}
            >
              <input
                id={`export-format-${option.value}`}
                className={styles.srOnly}
                type="radio"
                name="export-format"
                value={option.value}
                checked={format === option.value}
                disabled={isExporting}
                onChange={() => setFormat(option.value)}
                aria-label={`${option.label} — ${option.description}`}
                data-testid={`export-format-${option.value}`}
              />
              <span className={styles.formatIcon} aria-hidden>
                <i className={option.icon} />
              </span>
              <span className={styles.formatCopy}>
                <span className={styles.formatLabel}>{option.label}</span>
                <span className={styles.formatDescription}>{option.description}</span>
              </span>
            </label>
          ))}
        </div>

        <span className={styles.srOnly} data-testid="export-table-scope">
          {`A exportação considera a visualização atual de ${listName}: filtros, colunas visíveis e ordenação.`}
        </span>
      </form>
    </Dialog>
  );
}
